import type { PublicLinkPageProviderData } from '@confluence/public-links';
import type { ConfluenceEdition } from '@confluence/session-data';

import type { Principal } from '../../controlled/commonTypes';
import type { ContentRestrictionsQueryData, QueriesState } from '../Controller';
import type { ContentType } from '../../shareAndRestrictButton/ShareRestrictAndCopyCoordinator';

import { getCurrentUserPrincipal } from './getCurrentUserPrincipal';
import { getGeneralAccessLevel, getPrincipalsAndRoles } from './getContentRestrictions';
import { canSetRestrictions, getClosestAncestorWithViewRestrictions } from './dataToQueriesState';
import type {
	useQueriesShareAndRestrictDialogQuery_content_PaginatedContentListWithChild as ContentData,
	useQueriesShareAndRestrictDialogQuery_Query as QueryData,
} from './__types__/useQueriesShareAndRestrictDialogQuery';

type OperationType = 'read' | 'update';

// If you add any new fields to this, be sure to test that the USD / PublishModal integration still works.
export interface PublishModalUserInput {
	type: 'known' | 'unknown' | 'anonymous' | 'user';
	accountId: string;
	displayName: string;
	publicName: string;
	profilePicture: {
		path: string;
	};
	isExternalCollaborator?: boolean;
	email: string;
}

// If you add any new fields to this, be sure to test that the USD / PublishModal integration still works.
export interface PublishModalGroupInput {
	type: 'group';
	name: string;
	id: string;
}

export interface ContentRestriction {
	operation: OperationType;
	restrictions: {
		user: {
			results: PublishModalUserInput[];
		};
		group: {
			results: PublishModalGroupInput[];
		};
	};
}

export type PublishModalRestrictionsInput = ContentRestriction[];

type UsersAndGroups = {
	users: Principal[];
	groups: Principal[];
};

type ReadAndUpdate = {
	read: UsersAndGroups;
	update: UsersAndGroups;
};

export function publishModalDataToQueriesState({
	publishModalRestrictions,
	spaceKey,
	publicLinkPageData,
	data,
	contentType,
	edition,
}: {
	publishModalRestrictions: PublishModalRestrictionsInput | undefined;
	publicLinkPageData: PublicLinkPageProviderData;
	spaceKey: string;
	data: QueryData;
	contentType: ContentType;
	edition: ConfluenceEdition;
}): QueriesState | null {
	if (!publishModalRestrictions) return null;

	const currentUser = getCurrentUserPrincipal(data.user);
	if (!currentUser) return null;

	const contentData = getContentData(publishModalRestrictions, data.content, currentUser);
	if (!contentData) return null;

	return {
		...contentData,
		spaceKey,
		currentUser,
		publicLinkPageData,
		userCanEditRestrictions: canSetRestrictions(
			data,
			contentData.contentRestrictions.generalAccessLevel,
			contentType,
		),
		edition,
		currentUserHasAccess: contentData.currentUserHasAccess,
		disableSharingToEmails: false,
	};
}

function getContentData(
	publishModalRestrictions: PublishModalRestrictionsInput,
	content: ContentData | null,
	currentUser: Principal,
): Pick<
	QueriesState,
	| 'contentRestrictions'
	| 'hasInheritedRestrictions'
	| 'parentPageLink'
	| 'groupCount'
	| 'numberOfAncestors'
	| 'currentUserHasAccess'
> | null {
	if (!content) return null;

	const { nodes } = content;
	if (!nodes) return null;

	const [node0] = nodes;
	if (!node0) return null;

	const { hasInheritedRestrictions, ancestors } = node0;

	const contentRestrictionsAndCounts = getContentRestrictions(
		publishModalRestrictions,
		currentUser,
	);

	const parentPageLink = hasInheritedRestrictions
		? getClosestAncestorWithViewRestrictions(ancestors)
		: null;

	return {
		contentRestrictions: {
			generalAccessLevel: contentRestrictionsAndCounts.generalAccessLevel,
			principals: contentRestrictionsAndCounts.principals,
		},
		numberOfAncestors: ancestors?.length || 0,
		groupCount: contentRestrictionsAndCounts.groupCount,
		currentUserHasAccess: contentRestrictionsAndCounts.currentUserHasAccess,
		hasInheritedRestrictions,
		parentPageLink,
	};
}

function getContentRestrictions(
	publishModalRestrictions: PublishModalRestrictionsInput,
	currentUser: Principal,
): ContentRestrictionsQueryData & { groupCount: number; currentUserHasAccess: boolean } {
	const readAndUpdateLists = convertPublishModalRestrictions(publishModalRestrictions);
	const { principals, groupCount, currentUserHasAccess } = getPrincipalsAndRoles(
		readAndUpdateLists,
		currentUser,
	);

	return {
		generalAccessLevel: getGeneralAccessLevel(readAndUpdateLists),
		principals,
		groupCount,
		currentUserHasAccess,
	};
}

function convertPublishModalRestrictions(publishModalRestrictions: PublishModalRestrictionsInput) {
	const ret: ReadAndUpdate = {
		read: { users: [], groups: [] },
		update: { users: [], groups: [] },
	};

	const { read, update } = ret;

	for (const data of publishModalRestrictions) {
		const { operation } = data;

		if (operation === 'read') {
			convertAndPushUsers({
				users: data.restrictions.user.results,
				destination: read.users,
			});
			convertAndPushGroups({
				groups: data.restrictions.group.results,
				destination: read.groups,
			});
		}

		if (operation === 'update') {
			convertAndPushUsers({
				users: data.restrictions.user.results,
				destination: update.users,
			});
			convertAndPushGroups({
				groups: data.restrictions.group.results,
				destination: update.groups,
			});
		}
	}

	return ret;
}

function convertAndPushGroups({
	groups,
	destination,
}: {
	groups: PublishModalGroupInput[];
	destination: Principal[];
}) {
	for (const group of groups) {
		if (!group) continue;
		destination.push({
			type: 'group',
			id: group.id,
			name: group.name,
			// TODO: Update this to correctly distinguish between teams and groups in the publish modal.
			usageType: null,
		});
	}
}

function convertAndPushUsers({
	users,
	destination,
}: {
	users: PublishModalUserInput[];
	destination: Principal[];
}) {
	for (const user of users) {
		if (!user) continue;
		destination.push({
			type: 'user',
			id: user.accountId,
			avatarUrl: user.profilePicture?.path || '',
			name: user.displayName,
			email: user.email,
			knownErrors: undefined,
			sitePermissionType: 'INTERNAL',
		});
	}
}
